/**
 * Cleaning & Checklists — tick rows, then SUBMIT (OMS stamps who/when).
 *
 * Draft: stepKey → `true` (Done) / `false` (not yet) / `{ status: 'na',
 * naReason }` / `{ status: 'issue', issue, action }`. Tapping a step is still
 * Done; N/A and Issue are secondary. Express turns each Issue step into a
 * checklist_issue exception carrying its action (resolved on save).
 */

import {
  FOOD_SAFETY_CHECKLIST_ISSUE_MAX_LENGTH,
  FOOD_SAFETY_CHECKLIST_NA_REASON_MAX_LENGTH,
  FOOD_SAFETY_CHECKLIST_STEP_STATUS,
  FOOD_SAFETY_EXCEPTION_KIND,
} from 'src/constants/foodSafety'
import {
  EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT,
  buildFoodSafetyCorrectiveActionBody,
  validateFoodSafetyCorrectiveDraft,
} from 'src/utils/foodSafetyCorrectiveAction'
import {
  findNextOpenFridgeDefinitionId,
  formatFridgeDayProgressLabel,
  summarizeFridgeDayProgress,
} from 'src/utils/foodSafetyFridges'
import { formatFoodSafetyClockTime } from 'src/utils/foodSafetyTime'

export const findNextOpenChecklistId = findNextOpenFridgeDefinitionId
export const summarizeCleaningDayProgress = summarizeFridgeDayProgress
export const formatCleaningDayProgressLabel = formatFridgeDayProgressLabel

/**
 * @param {{ key?: string, label?: string }[]|null|undefined} steps
 */
export function normalizeChecklistSteps(steps) {
  if (!Array.isArray(steps)) return []
  return steps
    .map((step, index) => {
      const key = String(step?.key || `step_${index + 1}`).trim()
      const label = String(step?.label || key).trim()
      if (!key || !label) return null
      return { key, label }
    })
    .filter(Boolean)
}

/**
 * Draft: map of stepKey → boolean
 */
export function emptyChecklistDraft(steps) {
  const draft = {}
  for (const step of normalizeChecklistSteps(steps)) {
    draft[step.key] = false
  }
  return draft
}

const { DONE, NA, ISSUE } = FOOD_SAFETY_CHECKLIST_STEP_STATUS

export {
  FOOD_SAFETY_CHECKLIST_ISSUE_MAX_LENGTH,
  FOOD_SAFETY_CHECKLIST_NA_REASON_MAX_LENGTH,
}

/** done / na / issue for a draft value, or null when not answered yet. */
export function checklistDraftStepStatus(value) {
  if (value === true) return DONE
  if (value && typeof value === 'object') {
    if (value.status === NA) return NA
    if (value.status === ISSUE) return ISSUE
  }
  return null
}

/** Tap = Done; tapping a Done step clears it; N/A / Issue → Done. */
export function toggleChecklistStep(draft, stepKey) {
  return {
    ...(draft || {}),
    [stepKey]: draft?.[stepKey] !== true,
  }
}

export function markChecklistStepNa(draft, stepKey) {
  return { ...(draft || {}), [stepKey]: { status: NA, naReason: '' } }
}

export function markChecklistStepIssue(draft, stepKey) {
  return {
    ...(draft || {}),
    [stepKey]: {
      status: ISSUE,
      issue: '',
      action: { ...EMPTY_FOOD_SAFETY_CORRECTIVE_DRAFT },
    },
  }
}

/** Back to "not answered". */
export function clearChecklistStep(draft, stepKey) {
  return { ...(draft || {}), [stepKey]: false }
}

/** Merge into an N/A / Issue step (`action` patches merge too). */
export function patchChecklistStep(draft, stepKey, patch) {
  const current = draft?.[stepKey]
  if (!current || typeof current !== 'object') return draft || {}
  const next = { ...current, ...patch }
  if (patch?.action)
    next.action = { ...(current.action || {}), ...patch.action }
  return { ...(draft || {}), [stepKey]: next }
}

/** Every step answered (Done, N/A or Issue). */
export function allChecklistStepsTicked(steps, draft) {
  const list = normalizeChecklistSteps(steps)
  if (list.length === 0) return false
  return list.every(step => checklistDraftStepStatus(draft?.[step.key]))
}

/** Steps answered so far (Done, N/A or Issue). */
export function countTickedSteps(steps, draft) {
  const list = normalizeChecklistSteps(steps)
  return list.filter(step => checklistDraftStepStatus(draft?.[step.key])).length
}

/** "2 of 3 ticked", or "3 of 3 · 1 N/A · 1 issue" once N/A / Issue are used. */
export function formatChecklistProgressHint(steps, draft) {
  const list = normalizeChecklistSteps(steps)
  let na = 0
  let issues = 0
  for (const step of list) {
    const status = checklistDraftStepStatus(draft?.[step.key])
    if (status === NA) na += 1
    if (status === ISSUE) issues += 1
  }
  const answered = countTickedSteps(list, draft)
  if (!na && !issues) return `${answered} of ${list.length} ticked`
  return [
    `${answered} of ${list.length}`,
    na ? `${na} N/A` : null,
    issues ? `${issues} issue${issues === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** Issue steps with no action chosen — they save and stay open. */
export function countChecklistIssuesWithoutAction(steps, draft) {
  return normalizeChecklistSteps(steps).filter(step => {
    const value = draft?.[step.key]
    return checklistDraftStepStatus(value) === ISSUE && !value.action?.type
  }).length
}

/**
 * First Issue step missing what happened, or with an action that isn't
 * finished (e.g. "Other" with no note). No action is fine: it stays open.
 * @returns {{ stepKey: string, field: 'issue'|'action', message: string }|null}
 */
export function findChecklistStepError(steps, draft) {
  for (const step of normalizeChecklistSteps(steps)) {
    const value = draft?.[step.key]
    if (checklistDraftStepStatus(value) !== ISSUE) continue
    if (!String(value.issue || '').trim()) {
      return {
        stepKey: step.key,
        field: 'issue',
        message: 'Say what happened.',
      }
    }
    const actionError = validateFoodSafetyCorrectiveDraft(value.action || {})
    if (actionError) {
      return { stepKey: step.key, field: 'action', message: actionError }
    }
  }
  return null
}

/**
 * Build payload.stepCompletions for POST /logs.
 * @param {{ key: string, label: string }[]} steps
 * @param {Record<string, boolean>} draft
 * @param {Date|string} [completedAt]
 */
export function buildStepCompletions(steps, draft, completedAt = new Date()) {
  const at = completedAt instanceof Date ? completedAt : new Date(completedAt)
  return normalizeChecklistSteps(steps).map(step => {
    const value = draft?.[step.key]
    const base = { key: step.key, label: step.label }
    const status = checklistDraftStepStatus(value)
    if (status === DONE) {
      return {
        ...base,
        status: DONE,
        completed: true,
        completedAt: at.toISOString(),
      }
    }
    if (status === NA) {
      const naReason = String(value.naReason || '').trim()
      return {
        ...base,
        status: NA,
        completed: false,
        ...(naReason ? { naReason } : {}),
      }
    }
    if (status === ISSUE) {
      return {
        ...base,
        status: ISSUE,
        completed: false,
        issue: String(value.issue || '').trim(),
        correctiveAction: buildFoodSafetyCorrectiveActionBody(
          value.action || {},
        ),
      }
    }
    return { ...base, completed: false, completedAt: undefined }
  })
}

/** Staff row for an open job that is scheduled this day: “Due today”, or “Due this day”. */
export function formatCleaningJobStaffMeta(item, { viewingToday = true } = {}) {
  if (item?.completed) return null
  if (item?.expectation?.due === false) return null
  return viewingToday ? 'Due today' : 'Due this day'
}

/**
 * Day-card line. A checklist reads “Due by 22:00 · Checklist · 3 steps”.
 * A job reads “Due today · Job”.
 */
export function formatCleaningDayCardMeta({
  isJob = false,
  completed = false,
  dueLabel = null,
  stepCount = 0,
  viewingToday = true,
  item = null,
} = {}) {
  if (isJob) {
    const jobMeta = formatCleaningJobStaffMeta(item, { viewingToday })
    return [jobMeta, 'Job'].filter(Boolean).join(' · ')
  }
  const count = Number(stepCount) || 0
  return [
    !completed && dueLabel ? dueLabel : null,
    'Checklist',
    count > 0 ? `${count} step${count === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/** One sign-off. No steps, and no person — Express stamps the logged-in user. */
export function buildCleaningJobLogBody({ definitionId, localDate }) {
  return {
    definitionId,
    localDate,
    payload: {},
  }
}

export function buildChecklistLogBody({
  definitionId,
  localDate,
  steps,
  draft,
  notes,
}) {
  const payload = {
    stepCompletions: buildStepCompletions(steps, draft),
  }
  const note = String(notes || '').trim()
  if (note) payload.notes = note
  return {
    definitionId,
    localDate,
    payload,
  }
}

export function validateChecklistReadyToSubmit(steps, draft) {
  const list = normalizeChecklistSteps(steps)
  if (list.length === 0) {
    return 'This checklist has no steps yet — ask a manager to add some.'
  }
  if (!allChecklistStepsTicked(list, draft)) {
    const done = countTickedSteps(list, draft)
    return `Tick all steps before submit — or mark them N/A / Issue (${done} of ${list.length}).`
  }
  const stepError = findChecklistStepError(list, draft)
  if (stepError) {
    const label = list.find(step => step.key === stepError.stepKey)?.label
    return `${label}: ${stepError.message}`
  }
  return null
}

/** "Deep clean done", "… — 1 issue recorded", or "… — 1 issue needs corrective action". */
export function formatChecklistSavedMessage(name, steps, draft) {
  const list = normalizeChecklistSteps(steps)
  const issues = list.filter(
    step => checklistDraftStepStatus(draft?.[step.key]) === ISSUE,
  ).length
  const label = `${name || 'Checklist'} done`
  if (!issues) return label
  const open = countChecklistIssuesWithoutAction(list, draft)
  if (open) {
    return `${label} — ${open} issue${open === 1 ? '' : 's'} need${
      open === 1 ? 's' : ''
    } corrective action`
  }
  return `${label} — ${issues} issue${issues === 1 ? '' : 's'} recorded`
}

/** Saved step status — mirrors Express (V1 rows: `completed !== false` is Done). */
export function resolveChecklistLoggedStepStatus(step) {
  if ([DONE, NA, ISSUE].includes(step?.status)) return step.status
  if (!step) return null
  return step.completed === false ? null : DONE
}

/** The checklist_issue exception Express opened for a saved Issue step. */
export function findChecklistStepException(log, stepKey) {
  const list = Array.isArray(log?.exceptions) ? log.exceptions : []
  return (
    list.find(
      exception =>
        exception?.kind === FOOD_SAFETY_EXCEPTION_KIND.CHECKLIST_ISSUE &&
        String(exception?.context?.stepKey || exception?.dedupeKey || '') ===
          String(stepKey),
    ) || null
  )
}

/** "Done · 1 issue" badge for a completed checklist. */
export function formatChecklistDoneBadge(log, { lateDue = null } = {}) {
  const steps = log?.payload?.stepCompletions || []
  const issues = steps.filter(
    step => resolveChecklistLoggedStepStatus(step) === ISSUE,
  ).length
  const done = lateDue ? `Done late (due ${lateDue})` : 'Done'
  return issues ? `${done} · ${issues} issue${issues === 1 ? '' : 's'}` : done
}

/**
 * Zero-step checklist copy (29.23) — managers get a Setup CTA; chefs get honesty.
 * @param {{ canManage?: boolean }} [opts]
 * @returns {{ line: string, ctaLabel: string|null }}
 */
export function resolveCleaningEmptyStepsCopy({ canManage = false } = {}) {
  if (canManage) {
    return {
      line: 'No steps on this checklist yet — add them in Setup.',
      // 29.35 — verb family with section empty CTAs (Add …), not “Edit in Setup”
      ctaLabel: 'Add steps',
    }
  }
  return {
    line: 'No steps on this checklist yet — ask a manager to add them.',
    ctaLabel: null,
  }
}

/**
 * Format a completed log for the receipt line (no initials — name + time only).
 */
export function formatChecklistCompletedMeta(log) {
  const who = log?.completedByName || 'Logged'
  const time = log?.completedAt
    ? formatFoodSafetyClockTime(log.completedAt)
    : ''
  return [who, time].filter(Boolean).join(' · ')
}
